/**
 * <ec-price-range>
 *
 * Double-handle range slider that submits Shopify's native price filter
 * (filter.v.price.gte / filter.v.price.lte) via the parent <form>.
 *
 * Rendering: custom thumbs (divs) positioned by JS via CSS variables on
 * the slider container. The two <input type="range"> elements are kept
 * (opacity: 0) for keyboard accessibility — Tab focuses them, arrow keys
 * change the value. Pointer drag is implemented in JS directly on the
 * slider so we sidestep the visual glitches of two stacked native inputs.
 *
 * Markup contract (provided by snippets/ec-facets.liquid):
 *   <ec-price-range data-min data-max data-current-min data-current-max ...>
 *     <input type="hidden" data-ec-hidden="gte" name="filter.v.price.gte">
 *     <input type="hidden" data-ec-hidden="lte" name="filter.v.price.lte">
 *     <div class="ec-price-range__slider">
 *       <span class="ec-price-range__track-bg"></span>
 *       <span class="ec-price-range__track" data-ec-track></span>
 *       <div class="ec-price-range__thumb" data-ec-thumb="min"></div>
 *       <div class="ec-price-range__thumb" data-ec-thumb="max"></div>
 *       <input type="range" data-ec-range="min" ...>
 *       <input type="range" data-ec-range="max" ...>
 *     </div>
 *     <div class="ec-price-range__bounds">
 *       <span data-ec-bound="min"></span><span data-ec-bound="max"></span>
 *     </div>
 *     <input type="number" data-ec-input="min" ...>
 *     <input type="number" data-ec-input="max" ...>
 *   </ec-price-range>
 *
 * Values use the same display unit (e.g. EUR) end-to-end: data-* attributes,
 * inputs, and hidden inputs all carry decimal currency units. Shopify's
 * filter.v.price.gte/lte URL params accept that decimal format directly —
 * no cents-conversion needed at submit time.
 *
 * Submission: writes the value into the hidden inputs and dispatches a
 * debounced change event. <ec-filters> picks it up and runs
 * form.requestSubmit() on desktop; on mobile the user taps "Voir N…".
 */

const AUTO_SUBMIT_DELAY = 250;
const MIN_GAP = 1;

class EcPriceRange extends HTMLElement {
  #min = 0;
  #max = 0;
  #currentMin = 0;
  #currentMax = 0;
  #step = 1;
  #form = null;
  #slider = null;
  #rangeMin = null;
  #rangeMax = null;
  #numberMin = null;
  #numberMax = null;
  #hiddenGte = null;
  #hiddenLte = null;
  #boundLow = null;
  #boundHigh = null;
  #autoSubmitTimer = null;
  #onRangeInput = null;
  #onNumberInput = null;
  #onNumberChange = null;
  #onPointerDown = null;
  #onPointerMove = null;
  #onPointerUp = null;
  #dragTarget = null;

  // After an AJAX section morph the same element is reused (no new
  // connectedCallback) but data-current-min/max change. Observing them
  // re-syncs the internal state with the new server values so the
  // thumbs/track repaint correctly.
  static get observedAttributes() {
    return ['data-min', 'data-max', 'data-current-min', 'data-current-max'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (!this.#slider) return; // not initialized yet — connectedCallback will paint
    if (name === 'data-min') {
      this.#min = numberAttr(this, 'data-min', this.#min);
    } else if (name === 'data-max') {
      this.#max = numberAttr(this, 'data-max', this.#max);
    } else if (name === 'data-current-min') {
      this.#currentMin = numberAttr(this, 'data-current-min', this.#min);
    } else if (name === 'data-current-max') {
      this.#currentMax = numberAttr(this, 'data-current-max', this.#max);
    }
    this.#paintTrack();
  }

  /**
   * Resync state from data-* attributes and repaint. Public API for
   * <ec-filters> to call after a section morph — attributeChangedCallback
   * is not always fired by morph helpers in a way we can rely on, so this
   * method is the explicit "AJAX refresh" entry point.
   */
  refreshState() {
    if (!this.#slider) return;
    this.#min = numberAttr(this, 'data-min', 0);
    this.#max = numberAttr(this, 'data-max', 100);
    this.#currentMin = numberAttr(this, 'data-current-min', this.#min);
    this.#currentMax = numberAttr(this, 'data-current-max', this.#max);
    this.#paintTrack();
  }

  connectedCallback() {
    this.#min = numberAttr(this, 'data-min', 0);
    this.#max = numberAttr(this, 'data-max', 100);
    this.#currentMin = numberAttr(this, 'data-current-min', this.#min);
    this.#currentMax = numberAttr(this, 'data-current-max', this.#max);
    this.#step = numberAttr(this, 'data-step', 1);
    this.#form = this.closest('form');

    this.#slider = this.querySelector('.ec-price-range__slider');
    this.#rangeMin = this.querySelector('[data-ec-range="min"]');
    this.#rangeMax = this.querySelector('[data-ec-range="max"]');
    this.#numberMin = this.querySelector('[data-ec-input="min"]');
    this.#numberMax = this.querySelector('[data-ec-input="max"]');
    this.#hiddenGte = this.querySelector('[data-ec-hidden="gte"]');
    this.#hiddenLte = this.querySelector('[data-ec-hidden="lte"]');
    this.#boundLow = this.querySelector('[data-ec-bound="min"]');
    this.#boundHigh = this.querySelector('[data-ec-bound="max"]');

    if (!this.#slider || !this.#rangeMin || !this.#rangeMax) return;

    [this.#rangeMin, this.#rangeMax, this.#numberMin, this.#numberMax].forEach((input) => {
      if (input) input.dataset.ecPriceRangeSilent = 'true';
    });

    this.#onRangeInput = (event) => {
      const which = event.target?.dataset.ecRange;
      const value = parseFloat(event.target.value);
      if (Number.isNaN(value)) return;
      if (which === 'min') this.#setLow(value, { syncRange: false });
      else this.#setHigh(value, { syncRange: false });
      this.#queueSubmit();
    };

    this.#onNumberInput = (event) => {
      const which = event.target?.dataset.ecInput;
      const raw = event.target.value;
      if (raw === '') return;
      const value = parseFloat(raw);
      if (Number.isNaN(value)) return;
      if (which === 'min') this.#setLow(value, { syncNumber: false });
      else this.#setHigh(value, { syncNumber: false });
    };

    this.#onNumberChange = () => {
      this.#queueSubmit();
    };

    this.#onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      const rect = this.#slider.getBoundingClientRect();
      if (rect.width <= 0) return;
      const pct = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
      const lowPct = this.#valueToPct(this.#currentMin);
      const highPct = this.#valueToPct(this.#currentMax);
      // Pick the closer thumb. On a tie (clicking the middle), prefer the
      // thumb whose neighbour is on the side of the click.
      this.#dragTarget = Math.abs(pct - lowPct) <= Math.abs(pct - highPct) ? 'min' : 'max';
      this.#slider.setPointerCapture(event.pointerId);
      this.#applyPctTo(pct, this.#dragTarget);
      event.preventDefault();
    };

    this.#onPointerMove = (event) => {
      if (this.#dragTarget == null) return;
      if (event.pointerId == null) return;
      if (!this.#slider.hasPointerCapture(event.pointerId)) return;
      const rect = this.#slider.getBoundingClientRect();
      if (rect.width <= 0) return;
      const pct = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
      this.#applyPctTo(pct, this.#dragTarget);
    };

    this.#onPointerUp = (event) => {
      if (this.#dragTarget == null) return;
      this.#dragTarget = null;
      if (this.#slider.hasPointerCapture(event.pointerId)) {
        this.#slider.releasePointerCapture(event.pointerId);
      }
      this.#queueSubmit();
    };

    this.#rangeMin.addEventListener('input', this.#onRangeInput);
    this.#rangeMax.addEventListener('input', this.#onRangeInput);
    this.#numberMin?.addEventListener('input', this.#onNumberInput);
    this.#numberMax?.addEventListener('input', this.#onNumberInput);
    this.#numberMin?.addEventListener('change', this.#onNumberChange);
    this.#numberMax?.addEventListener('change', this.#onNumberChange);
    this.#slider.addEventListener('pointerdown', this.#onPointerDown);
    this.#slider.addEventListener('pointermove', this.#onPointerMove);
    this.#slider.addEventListener('pointerup', this.#onPointerUp);
    this.#slider.addEventListener('pointercancel', this.#onPointerUp);

    this.#paintTrack();
  }

  disconnectedCallback() {
    this.#rangeMin?.removeEventListener('input', this.#onRangeInput);
    this.#rangeMax?.removeEventListener('input', this.#onRangeInput);
    this.#numberMin?.removeEventListener('input', this.#onNumberInput);
    this.#numberMax?.removeEventListener('input', this.#onNumberInput);
    this.#numberMin?.removeEventListener('change', this.#onNumberChange);
    this.#numberMax?.removeEventListener('change', this.#onNumberChange);
    this.#slider?.removeEventListener('pointerdown', this.#onPointerDown);
    this.#slider?.removeEventListener('pointermove', this.#onPointerMove);
    this.#slider?.removeEventListener('pointerup', this.#onPointerUp);
    this.#slider?.removeEventListener('pointercancel', this.#onPointerUp);
    if (this.#autoSubmitTimer) {
      clearTimeout(this.#autoSubmitTimer);
      this.#autoSubmitTimer = null;
    }
  }

  #setLow(raw, opts = {}) {
    const clamped = Math.min(Math.max(raw, this.#min), this.#currentMax - MIN_GAP);
    this.#currentMin = clamped;
    if (opts.syncRange !== false && this.#rangeMin) this.#rangeMin.value = String(clamped);
    if (opts.syncNumber !== false && this.#numberMin) this.#numberMin.value = String(clamped);
    if (this.#hiddenGte) {
      this.#hiddenGte.value = clamped > this.#min ? String(clamped) : '';
    }
    this.#paintTrack();
  }

  #setHigh(raw, opts = {}) {
    const clamped = Math.max(Math.min(raw, this.#max), this.#currentMin + MIN_GAP);
    this.#currentMax = clamped;
    if (opts.syncRange !== false && this.#rangeMax) this.#rangeMax.value = String(clamped);
    if (opts.syncNumber !== false && this.#numberMax) this.#numberMax.value = String(clamped);
    if (this.#hiddenLte) {
      this.#hiddenLte.value = clamped < this.#max ? String(clamped) : '';
    }
    this.#paintTrack();
  }

  #valueToPct(value) {
    const range = this.#max - this.#min;
    if (range <= 0) return 0;
    return ((value - this.#min) / range) * 100;
  }

  #pctToValue(pct) {
    const range = this.#max - this.#min;
    return Math.round(this.#min + (pct / 100) * range);
  }

  #applyPctTo(pct, target) {
    const value = this.#pctToValue(pct);
    if (target === 'min') this.#setLow(value);
    else this.#setHigh(value);
  }

  #paintTrack() {
    if (!this.#slider) return;
    const lowPct = this.#valueToPct(this.#currentMin);
    const highPct = this.#valueToPct(this.#currentMax);
    this.#slider.style.setProperty('--ec-price-range-low', `${lowPct}%`);
    this.#slider.style.setProperty('--ec-price-range-high', `${100 - highPct}%`);
    // Bounds are absolute (min/max of the slider scale, not the active range);
    // they stay fixed across drags. The current values are already shown in
    // the number inputs below the slider — duplicating them on the bounds
    // makes the slider look "shrunk" when the user lowers a thumb.
  }

  #queueSubmit() {
    if (this.#autoSubmitTimer) clearTimeout(this.#autoSubmitTimer);
    this.#autoSubmitTimer = window.setTimeout(() => {
      this.#autoSubmitTimer = null;
      if (!this.#form) return;
      this.#hiddenGte?.dispatchEvent(new Event('change', { bubbles: true }));
    }, AUTO_SUBMIT_DELAY);
  }
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function numberAttr(el, name, fallback) {
  const raw = el.getAttribute(name);
  if (raw == null || raw === '') return fallback;
  const value = parseFloat(raw);
  return Number.isNaN(value) ? fallback : value;
}

function formatBound(el, value) {
  const prefix = el.getAttribute('data-currency-prefix') ?? '';
  const suffix = el.getAttribute('data-currency-suffix') ?? '';
  return `${prefix}${Math.round(value)}${suffix}`;
}

if (!customElements.get('ec-price-range')) {
  customElements.define('ec-price-range', EcPriceRange);
}
